import { HStack, Text } from '@ayble-health/dashboard-components';
import React from 'react';
import { useUserMeetings } from '../../api/healthCoach/useUserMeetings';
import { useParams } from 'react-router-dom';
import { DEFAULT_AVATAR_IMG_URL } from '../HealthCoachDashboard/constants';
import { VisitCard } from './VisitCard/VisitCard';
import { MeetingType } from '../../models/meeting';
import { computeNextMeetings } from './utils';
import { Box } from '@chakra-ui/react';
import { Loader } from '../Loader';
import { formatDateInTZ } from '../UserDashboard/utils';
import { useDashboardPageState } from '../../hooks/useDashboardPageState';
import { useUpdateMeetingStatus } from '../../api/healthCoach/mutations/useUpdateMeetingStatus';
import { PmStatus, ProviderType } from '../../api/types';

type Props = {
  highlightedMeetingId: number | null;
  onRescheduleMeeting: (
    meetingType: MeetingType,
    providerType: ProviderType,
    meetingId: number
  ) => void;
};

export const NextVisits = ({
  highlightedMeetingId,
  onRescheduleMeeting,
}: Props) => {
  const {
    state: { user },
  } = useDashboardPageState();
  const { patient } = useParams();
  const { meetings, isLoading, isError } = useUserMeetings(patient ?? '');
  const nextMeetings = computeNextMeetings(meetings ?? []);
  const { mutation: updateMeetingStatus } = useUpdateMeetingStatus();

  if (isLoading) {
    return (
      <Box p={0}>
        <Loader loading={true} />
      </Box>
    );
  }

  if (isError) return null;

  if (!nextMeetings?.length) {
    return (
      <HStack gap={4}>
        <Text>There are no upcoming meetings.</Text>
      </HStack>
    );
  }

  return (
    <HStack gap={4}>
      {nextMeetings.map(
        ({
          client_confirmed,
          meeting_date,
          healthie_appointment_id,
          meeting_type,
          provider,
        }) => {
          const {
            display_name = '',
            image_url = DEFAULT_AVATAR_IMG_URL,
            type: providerType = ProviderType.Coach,
          } = provider || {};

          return (
            <VisitCard
              avatarUrl={image_url}
              confirmed={client_confirmed}
              highlighted={highlightedMeetingId === healthie_appointment_id}
              hostDisplayName={display_name}
              key={healthie_appointment_id}
              loading={updateMeetingStatus.isLoading}
              meetingId={healthie_appointment_id}
              meetingTime={formatDateInTZ(meeting_date, user?.timezone)}
              meetingType={meeting_type as MeetingType}
              onCancel={async () => {
                await updateMeetingStatus.mutateAsync({
                  meetingId: healthie_appointment_id,
                  payload: { pm_status: PmStatus.cancelled },
                });
              }}
              onConfirm={async () => {
                await updateMeetingStatus.mutateAsync({
                  meetingId: healthie_appointment_id,
                  payload: { client_confirmed: true },
                });
              }}
              onReschedule={(meetingType: MeetingType, meetingId: number) =>
                onRescheduleMeeting(meetingType, providerType, meetingId)
              }
            />
          );
        }
      )}
    </HStack>
  );
};
